import React from "react";
import { Button, Space, Popconfirm, Tag } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { ButtonConfig } from "@ms/shared-components";
import {
  Space as SpaceType,
  SpaceType as SpaceTypeEnum,
  SpaceStatus,
  SpaceTag,
} from "../../../types/space";
// 空间类型配置
export const SPACE_TYPE_CONFIG = {
  [SpaceTypeEnum.BUILDING]: { text: "建筑", color: "orange", label: "建筑" },
  [SpaceTypeEnum.ZONE]: { text: "区域", color: "blue", label: "区域" },
  [SpaceTypeEnum.SPACE]: { text: "空间", color: "green", label: "空间" },
} as const;

// 空间状态配置
export const SPACE_STATUS_CONFIG = {
  [SpaceStatus.ACTIVE]: { text: "启用", color: "green", label: "启用" },
  [SpaceStatus.INACTIVE]: { text: "禁用", color: "red", label: "禁用" },
  [SpaceStatus.MAINTENANCE]: {
    text: "维护中",
    color: "orange",
    label: "维护中",
  },
} as const;

// 消息配置
export const MESSAGES = {
  // 操作确认消息
  CONFIRM: {
    DELETE: "确定要删除这个空间吗？",
    BATCH_DELETE_SUCCESS: "成功删除 {count} 条记录",
    BATCH_DELETE_FAIL: "删除失败 {count} 条记录",
  },

  // 成功消息
  SUCCESS: {
    LOAD: "加载成功",
    CREATE: "创建成功",
    UPDATE: "更新成功",
    DELETE: "删除成功",
    BATCH_DELETE: "批量删除成功",
  },

  // 错误消息
  ERROR: {
    LOAD: "加载空间数据失败",
    CREATE: "创建失败",
    UPDATE: "更新失败",
    DELETE: "删除失败",
    BATCH_DELETE: "批量删除失败",
    NETWORK: "网络错误",
  },
} as const;

// 为了保持向后兼容，提供单独的导出
export const CONFIRM_MESSAGES = MESSAGES.CONFIRM;
export const SUCCESS_MESSAGES = MESSAGES.SUCCESS;
export const ERROR_MESSAGES = MESSAGES.ERROR;

// 表格面板按钮配置（新版，用于 ContainerPanel）
export const createTablePanelButtons = (handlers: {
  loadSpaces: () => void;
  handleCreate: () => void;
}): ButtonConfig[] => [
  {
    key: "refresh",
    text: "刷新",
    icon: <ReloadOutlined />,
    onClick: () => {
      handlers.loadSpaces();
    },
    group: "primary",
  },
  {
    key: "create",
    text: "新建",
    icon: <PlusOutlined />,
    type: "primary",
    onClick: () => {
      handlers.handleCreate();
    },
    group: "primary",
  },
];

// 表格配置常量
export const TABLE_CONFIG = {
  // 分页配置
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: ["10", "20", "50", "100"],

  // 列宽配置
  MIN_COLUMN_WIDTH: 60,
  MAX_COLUMN_WIDTH: 300,

  // 存储键
  STORAGE_KEY: "space_list_table",

  // 表格标题
  TABLE_TITLE: "空间列表",
} as const;

// 生成表格标题（包含列设置按钮）
export const createTableTitle = (
  tableColumnButton: React.ReactNode | null
): string | React.ReactNode => {
  if (tableColumnButton) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span>{TABLE_CONFIG.TABLE_TITLE}</span>
        {tableColumnButton}
      </div>
    );
  }
  return TABLE_CONFIG.TABLE_TITLE;
};

// 搜索面板配置常量
export const SEARCH_PANEL_CONFIG = {
  // 搜索面板标题
  SEARCH_TITLE: "空间搜索",

  // 列数配置
  DEFAULT_COLUMNS: 4,

  // 标签宽度
  LABEL_WIDTH: 80,

  // 是否可折叠
  COLLAPSIBLE: true,

  // 默认折叠状态
  DEFAULT_COLLAPSED: false,
} as const;

// 操作按钮文本配置
export const BUTTON_TEXTS = {
  REFRESH: "刷新",
  CREATE: "新建",
  EDIT: "编辑",
  VIEW: "查看详情",
  DELETE: "删除",
} as const;

// 表格列配置
export const createTableColumns = (handlers: {
  handleViewDetail: (space: SpaceType) => void;
  handleEdit: (space: SpaceType) => void;
  handleDelete: (id: string) => void;
  handleAddChild: (parentSpace: SpaceType) => void;
  handleStatusToggle: (spaceId: string, currentStatus: SpaceStatus) => void;
}): ColumnsType<SpaceType> => [
  {
    title: "空间名称",
    dataIndex: "name",
    key: "name",
    width: 200,
    render: (name: string, record: SpaceType) => {
      const typeConfig = SPACE_TYPE_CONFIG[record.type];
      return (
        <div>
          <div style={{ marginBottom: 4 }}>{name}</div>
          <Tag color={typeConfig.color} style={{ margin: 0 }}>
            {typeConfig.text}
          </Tag>
        </div>
      );
    },
  },
  {
    title: "空间路径",
    dataIndex: "spacePath",
    key: "spacePath",
    width: 300,
    ellipsis: true,
    render: (spacePath: string | undefined, record: SpaceType) => {
      // 优先使用 spacePath，如果没有则显示空
      const path = spacePath || record.spacePath;
      if (!path) {
        return <span style={{ color: "#999" }}>-</span>;
      }
      return <span title={path}>{path}</span>;
    },
  },
  {
    title: "组织路径",
    dataIndex: "orgPath",
    key: "orgPath",
    width: 200,
    ellipsis: true,
    render: (orgPath: string | undefined) => {
      if (!orgPath) {
        return <span style={{ color: "#999" }}>-</span>;
      }
      return <span title={orgPath}>{orgPath}</span>;
    },
  },
  {
    title: "来自酒店",
    dataIndex: "fromHotel",
    key: "fromHotel",
    width: 120,
    render: (fromHotel: boolean | undefined) => {
      if (fromHotel) {
        return <Tag color="orange">是</Tag>;
      }
      return <Tag color="default">否</Tag>;
    },
  },
  {
    title: "状态",
    dataIndex: "status",
    key: "status",
    width: 100,
    render: (status: SpaceStatus, record: SpaceType) => {
      const config = SPACE_STATUS_CONFIG[status];
      return (
        <Tag
          color={config.color}
          style={{ cursor: "pointer" }}
          onClick={() => handlers.handleStatusToggle(record.id, status)}
        >
          {config.text}
        </Tag>
      );
    },
  },
  {
    title: "标签",
    dataIndex: "tags",
    key: "tags",
    width: 200,
    render: (tags: SpaceTag[]) => {
      if (!tags || tags.length === 0) {
        return <span style={{ color: "#999" }}>-</span>;
      }

      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {tags.map((tag, index) => (
            <Tag
              key={tag.id || index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                margin: "2px 0",
              }}
            >
              {tag.icon && (
                <div
                  className="list-tag-icon"
                  style={{
                    width: 14,
                    height: 14,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  dangerouslySetInnerHTML={{ __html: tag.icon }}
                />
              )}
              <span>{tag.name}</span>
            </Tag>
          ))}
        </div>
      );
    },
  },
  {
    title: "子空间数量",
    dataIndex: "_count",
    key: "childrenCount",
    width: 100,
    render: (_count: any) => {
      return _count?.children || 0;
    },
  },
  {
    title: "描述",
    dataIndex: "description",
    key: "description",
    ellipsis: true,
  },
  {
    title: "创建时间",
    dataIndex: "createdAt",
    key: "createdAt",
    width: 180,
    render: (date: string) => new Date(date).toLocaleString(),
  },
  {
    title: "操作",
    key: "action",
    width: 150,
    fixed: "right",
    render: (_, record) => (
      <Space size={4}>
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => handlers.handleViewDetail(record)}
          title="查看"
        />
        <Button
          type="link"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handlers.handleEdit(record)}
          title="编辑"
        />
        {/* 只有建筑和区域才显示添加子节点按钮 */}
        {(record.type === SpaceTypeEnum.BUILDING ||
          record.type === SpaceTypeEnum.ZONE) && (
          <Button
            type="link"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => handlers.handleAddChild(record)}
            title="添加子空间"
          />
        )}
        <Popconfirm
          title={CONFIRM_MESSAGES.DELETE}
          onConfirm={() => handlers.handleDelete(record.id)}
          okText="确定"
          cancelText="取消"
          disabled={record.fromHotel}
        >
          <Button
            type="link"
            size="small"
            danger
            icon={<DeleteOutlined />}
            title={record.fromHotel ? "来自酒店的空间不能删除" : "删除"}
            disabled={record.fromHotel}
          />
        </Popconfirm>
      </Space>
    ),
  },
];
